import { Button, Stack } from '@mui/material';
import { endOfMonth, format, isValid, parseISO, startOfDay } from 'date-fns';
import { Field, FormikProvider, useFormik } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { DatePickerField } from 'shared/ui/formik-fields/date-picker';
import { UserPickerField } from 'features/user/user-picker/ui';
import { useCallback, useEffect } from 'react';

type WorkShiftFilters = { dateFrom: Date | null; dateTo: Date | null; userId: number | null };

export const WorkShiftsFilter = ({ year, month }: { year: number; month: number }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateFrom = searchParams.get('dateFrom');
  const dateTo = searchParams.get('dateTo');
  const userId = searchParams.get('userId');
  const monthStart = new Date(year, month, 1);
  const monthEnd = endOfMonth(monthStart);
  const updateParams = useCallback((values: WorkShiftFilters) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      (['dateFrom', 'dateTo'] as const).forEach((key) => {
        const value = values[key];
        if (value) next.set(key, format(value, 'yyyy-MM-dd'));
        else next.delete(key);
      });
      if (values.userId !== null) next.set('userId', String(values.userId));
      else next.delete('userId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const formik = useFormik<WorkShiftFilters>({
    initialValues: {
      dateFrom: dateFrom ? parseISO(dateFrom) : null,
      dateTo: dateTo ? parseISO(dateTo) : null,
      userId: userId ? Number(userId) : null,
    },
    enableReinitialize: true,
    initialTouched: { dateFrom: true, dateTo: true, userId: true },
    validate: (values) => {
      const errors: { dateFrom?: string; dateTo?: string; userId?: string } = {};
      if (values.userId !== null && (!Number.isSafeInteger(values.userId) || values.userId <= 0)) {
        errors.userId = 'Некорректный сотрудник';
      }
      (['dateFrom', 'dateTo'] as const).forEach((key) => {
        const value = values[key];
        if (!value) return;
        if (!isValid(value)) errors[key] = 'Некорректная дата';
        else if (value < monthStart || value > monthEnd) errors[key] = 'Дата должна быть в выбранном месяце';
      });
      const start = values.dateFrom || monthStart;
      const today = startOfDay(new Date());
      const end = values.dateTo || (values.dateFrom && today < monthEnd ? today : monthEnd);
      if (!errors.dateFrom && !errors.dateTo && start > end) {
        errors.dateTo = 'Дата по должна быть не раньше даты с';
      }
      return errors;
    },
    onSubmit: () => {},
  });

  useEffect(() => {
    if (!formik.dirty || !formik.isValid || formik.isValidating) return;
    const timeout = setTimeout(() => updateParams(formik.values), 500);
    return () => clearTimeout(timeout);
  }, [formik.dirty, formik.isValid, formik.isValidating, formik.values, updateParams]);

  return (
    <FormikProvider value={formik}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2, maxWidth: 1100 }} alignItems="flex-start">
        <Field name="userId" label="Сотрудник" size="small" clearable component={UserPickerField} />
        <Field
          name="dateFrom" label="Дата с" size="small" clearable component={DatePickerField}
          minDate={monthStart} maxDate={monthEnd} referenceDate={monthStart} views={['day']} format="dd.MM.yyyy"
        />
        <Field
          name="dateTo" label="Дата по" size="small" clearable component={DatePickerField}
          minDate={monthStart} maxDate={monthEnd} referenceDate={monthStart} views={['day']} format="dd.MM.yyyy"
        />
        <Button variant="outlined" sx={{ minWidth: 120, height: 40, flexShrink: 0, width: { xs: '100%', md: 'auto' } }} onClick={() => {
          updateParams({ dateFrom: null, dateTo: null, userId: null });
          formik.resetForm({ values: { dateFrom: null, dateTo: null, userId: null } });
        }}>Сбросить</Button>
      </Stack>
    </FormikProvider>
  );
};
